import React, { useCallback } from 'react';
import { Button, Space } from 'antd';
import type { FP620, FP621 } from '../../../services/pwp252Api';

interface Tab2DetailsProps {
  formData: Partial<FP620>;
  details: Partial<FP621>[];
  shippingUnit: number; // 1=YDS, 2=MTS, 3=PCS
  isEditing: boolean;
  onChange: (details: Partial<FP621>[]) => void;
  onFieldChange: (field: keyof FP620, value: any) => void;
  gwVariation: number; // standalone fp621_442 default for new rows
  onGwVariationChange: (value: number) => void;
}

const Tab2Details: React.FC<Tab2DetailsProps> = ({ formData, details, shippingUnit, isEditing, onChange, onFieldChange, gwVariation, onGwVariationChange }) => {
  const isPCS = shippingUnit === 3;
  const ro = !isEditing;

  const handleDetailChange = useCallback((index: number, field: keyof FP621, value: any) => {
    const next = details.map((d, i) => i === index ? { ...d, [field]: value } : d);
    onChange(next);
  }, [details, onChange]);

  const handleAdd = useCallback(() => {
    // Inherit COLOR and 缸號 from last row
    const last = details[details.length - 1];
    const newRow: Partial<FP621> = {
      fp621_02: details.length + 1,
      fp621_11: '',
      fp621_12: last?.fp621_12 || '',
      fp621_13: last?.fp621_13 || '',
      fp621_14: 0,
      fp621_15: 0,
      fp621_16: 0,
      fp621_17: 0,
      fp621_18: 0,
      fp621_442: last?.fp621_442 || 0,
    };
    onChange([...details, newRow]);
  }, [details, onChange]);

  const handleDelete = useCallback((index: number) => {
    const next = details.filter((_, i) => i !== index).map((d, i) => ({ ...d, fp621_02: i + 1 }));
    onChange(next);
  }, [details, onChange]);

  return (
    <div className="pwp252-tab2">
      <div className="pwp252-detail-header">
        <div className="pwp252-field-row">
          <label>總項數</label>
          <input
            type="number"
            value={formData.fp620_18 || 0}
            readOnly
            disabled
            className="pwp252-input"
            style={{ width: 80 }}
          />
        </div>

        <div className="pwp252-field-row">
          <label>毛重變量</label>
          <input
            type="number"
            step="0.1"
            value={gwVariation}
            readOnly={ro}
            onChange={e => onGwVariationChange(parseFloat(e.target.value) || 0)}
            className="pwp252-input"
            style={{ width: 120 }}
          />
        </div>

        {!isPCS && (
          <div className="pwp252-field-row">
            <label>MTS（換算率）</label>
            <input
              type="number"
              step="0.0001"
              value={formData.fp620_22 ?? 0.9144}
              readOnly={ro}
              onChange={e => onFieldChange('fp620_22', parseFloat(e.target.value) || 0)}
              className="pwp252-input"
              style={{ width: 120 }}
            />
          </div>
        )}

        <div className="pwp252-field-row">
          <label>N.W.（換算率）</label>
          <input
            type="number"
            step="0.0001"
            value={formData.fp620_23 ?? 0}
            readOnly={ro}
            onChange={e => onFieldChange('fp620_23', parseFloat(e.target.value) || 0)}
            className="pwp252-input"
            style={{ width: 120 }}
          />
        </div>
      </div>

      {isEditing && (
        <div className="pwp252-detail-actions">
          <Space>
            <Button size="small" type="primary" onClick={handleAdd}>明細新增</Button>
          </Space>
        </div>
      )}

      <div className="pwp252-detail-table-wrapper">
        <table className="pwp252-detail-table">
          <thead>
            <tr>
              <th>項數</th>
              <th>R/NO</th>
              <th>COLOR</th>
              <th>缸號</th>
              <th>{isPCS ? 'PCS' : 'YDS'}</th>
              <th>N.W.</th>
              <th>G.W.</th>
              {!isPCS && <th>MTS</th>}
              <th>補布</th>
              <th>G.W.加量</th>
              {isEditing && <th>操作</th>}
            </tr>
          </thead>
          <tbody>
            {details.length === 0 && (
              <tr>
                <td colSpan={isEditing ? 11 : 10} style={{ textAlign: 'center', color: '#999' }}>
                  尚無明細資料
                </td>
              </tr>
            )}
            {details.map((d, idx) => (
              <tr key={idx}>
                <td>{d.fp621_02 ?? idx + 1}</td>
                <td>
                  <input
                    type="text"
                    value={d.fp621_11 || ''}
                    readOnly={!isEditing}
                    onChange={e => handleDetailChange(idx, 'fp621_11', e.target.value)}
                    className="pwp252-detail-input"
                    style={{ width: 70 }}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={d.fp621_12 || ''}
                    readOnly={!isEditing}
                    onChange={e => handleDetailChange(idx, 'fp621_12', e.target.value)}
                    className="pwp252-detail-input"
                    style={{ width: 120 }}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={d.fp621_13 || ''}
                    readOnly={!isEditing}
                    onChange={e => handleDetailChange(idx, 'fp621_13', e.target.value)}
                    className="pwp252-detail-input"
                    style={{ width: 80 }}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    step="0.1"
                    value={d.fp621_14 ?? 0}
                    readOnly={!isEditing}
                    onChange={e => handleDetailChange(idx, 'fp621_14', parseFloat(e.target.value) || 0)}
                    className="pwp252-detail-input"
                    style={{ width: 80 }}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    step="0.1"
                    value={d.fp621_15 ?? 0}
                    readOnly={!isEditing}
                    onChange={e => handleDetailChange(idx, 'fp621_15', parseFloat(e.target.value) || 0)}
                    className="pwp252-detail-input"
                    style={{ width: 75 }}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    step="0.1"
                    value={d.fp621_16 ?? 0}
                    readOnly
                    disabled={isEditing}
                    className="pwp252-detail-input pwp252-readonly"
                    style={{ width: 75 }}
                  />
                </td>
                {!isPCS && (
                  <td>
                    <input
                      type="number"
                      step="0.1"
                      value={d.fp621_17 ?? 0}
                      readOnly={!isEditing}
                      onChange={e => handleDetailChange(idx, 'fp621_17', parseFloat(e.target.value) || 0)}
                      className="pwp252-detail-input"
                      style={{ width: 75 }}
                    />
                  </td>
                )}
                <td>
                  <input
                    type="number"
                    step="0.1"
                    value={d.fp621_18 ?? 0}
                    readOnly={!isEditing}
                    onChange={e => handleDetailChange(idx, 'fp621_18', parseFloat(e.target.value) || 0)}
                    className="pwp252-detail-input"
                    style={{ width: 60 }}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    step="0.1"
                    value={d.fp621_442 ?? 0}
                    readOnly={!isEditing}
                    onChange={e => handleDetailChange(idx, 'fp621_442', parseFloat(e.target.value) || 0)}
                    className="pwp252-detail-input"
                    style={{ width: 70 }}
                  />
                </td>
                {isEditing && (
                  <td>
                    <Button
                      size="small"
                      danger
                      onClick={() => handleDelete(idx)}
                    >
                      刪除
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Tab2Details;
